import {
  Action,
  ButtonActionProps,
  closeCurrentDialog,
  openConfirmDialog,
  openDialog,
  showNotification,
} from 'platform/components';
import {match} from 'ts-pattern';

import {always, isNil} from 'ramda';
import {isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {PaymentStateType, TaxDocumentForPaymentResponseBody} from '@dms/api/core';
import {ContractInformationResponseBodyV2, useGetCustomerV2Query} from '@dms/api/customer';
import {useReplaceInvoiceCustomerMutation} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {accountingRoutes, testIds} from '@dms/routes';
import {
  BillingInformationListDialog,
  DownloadAccountingDocument,
  handleApiError,
  hasAccountingDocumentDocxRendition,
  InvoicePaymentForm,
  InvoiceTypes,
  useCancelAccountingDocument,
  useDownloadAccountingDocument,
  useGetDocumentData,
  usePermissions,
} from '@dms/shared';

import {buildArray, composePath, Nullish} from 'shared/web';

import {convertContractInformationToInvoiceCustomer} from '../utils/convertContractInformationToInvoiceCustomer';
import {isCanceledOrCanceling} from '../utils/isCanceledOrCanceling';

// invoice types eligible for the "Create corrective document" subheader action
const INVOICE_TYPES_THAT_CAN_CREATE_CORRECTIVE_DOCUMENT: InvoiceTypes[] = [
  'ISSUED',
  'BALANCE_INVOICE',
];

// invoice types eligible for the "Pay" subheader action, combined with PAYMENT_STATES_THAT_CAN_BE_PAID below
const INVOICE_TYPES_THAT_CAN_BE_PAID: InvoiceTypes[] = [
  'ISSUED',
  'BALANCE_INVOICE',
  'CORRECTIVE_TAX_DOCUMENT',
];

// invoice types eligible for the "Change billing information" subheader action
// currently the same set as INVOICE_TYPES_THAT_CAN_CREATE_CORRECTIVE_DOCUMENT, but each gates
// a distinct action and may diverge, so they are kept as separate constants
const INVOICE_TYPES_THAT_CAN_CHANGE_BILLING_INFO: InvoiceTypes[] = ['ISSUED', 'BALANCE_INVOICE'];

// payment states eligible for the "Pay" subheader action, combined with INVOICE_TYPES_THAT_CAN_BE_PAID above
const PAYMENT_STATES_THAT_CAN_BE_PAID: PaymentStateType[] = [
  'overdue',
  'partially_paid',
  'reopened',
  'confirmed',
];

// The title names the file format, which reads the same in every language.
const DOCX_ACTION_TITLE = 'DOCX';

export const useDocumentActions = (documentType: InvoiceTypes, documentId: string): [Action[]] => {
  const {data: documentData} = useGetDocumentData(documentType, documentId);
  const [downloadDocument, {isLoading: isDownloading}] = useDownloadAccountingDocument();
  const [downloadDocumentAsDocx, {isLoading: isDownloadingDocx}] =
    useDownloadAccountingDocument('downloadDocx');
  const [printDocument, {isLoading: isPrinting}] = useDownloadAccountingDocument('print');
  const [cancelDocument, {isLoading: isCancelling}] = useCancelAccountingDocument();
  const [replaceInvoiceCustomer] = useReplaceInvoiceCustomerMutation();

  const isDocumentFromAccountingModule = documentData?.documentSource.moduleSource === 'accounting';

  const [
    canCreateInvoice,
    canDownloadDocuments,
    canCancelInvoice,
    canPayInvoice,
    canCreateCorrectiveTaxDocumentInvoice,
    canRenderMarginInvoicePrint,
  ] = usePermissions({
    permissionKeys: [
      'createInvoice',
      'downloadDocuments',
      'cancelInvoice',
      'payInvoice',
      'createCorrectiveTaxDocumentInvoice',
      'renderMarginInvoicePrint',
    ],
  });

  const {data: customerData} = useGetCustomerV2Query(
    {customerId: documentData?.customer?.id ?? ''},
    {skip: isNilOrEmpty(documentData?.customer?.id)}
  );

  const invoiceCategory = documentData?.invoiceCategory as 'default' | 'internal' | null;

  const outputDocument = (output: DownloadAccountingDocument, isMargin: boolean) =>
    output(
      documentType,
      documentId,
      documentData?.documentSource?.moduleSource,
      documentData?.number,
      invoiceCategory,
      isMargin
    );

  const handleCancelDocument = () =>
    openConfirmDialog({
      onConfirm: () => cancelDocument(documentType, documentId),
      'data-testid': testIds.accounting.invoiceDetail('cancel-invoice-dialog'),
      text: i18n.t('page.accounting.invoiceDetail.cancelDialogText', {
        invoiceNumber: documentData?.number,
      }),
    });

  const handleOpenNewCorrectiveTaxDocument = () => {
    window.open(
      composePath(accountingRoutes.newCorrectiveTaxDocument, {
        params: {id: documentId},
        queryParams: {invoiceType: documentType},
      }),
      '_blank'
    );
  };

  const handleUpdateBillingInformation = (data: ContractInformationResponseBodyV2) => {
    if (isNil(customerData?.id)) {
      showNotification.error();
      return;
    }

    replaceInvoiceCustomer({
      invoiceId: documentId,
      ...convertContractInformationToInvoiceCustomer(customerData?.id ?? '', data),
    })
      .unwrap()
      .then(closeCurrentDialog)
      .catch(handleApiError);
  };

  const openBillingInformationDialog = () => {
    openDialog(
      <BillingInformationListDialog
        customerId={documentData?.customer?.id ?? ''}
        onSave={handleUpdateBillingInformation}
        onClose={closeCurrentDialog}
        data-testid={testIds.accounting.invoiceDetail('BillingInformationList')}
      />,
      {
        scrollBehavior: 'outside',
        'data-testid': testIds.accounting.invoiceDetail('BillingInformationListDialog'),
      }
    );
  };

  const canBePaid =
    documentData?.paymentState &&
    INVOICE_TYPES_THAT_CAN_BE_PAID.includes(documentType) &&
    PAYMENT_STATES_THAT_CAN_BE_PAID.includes(documentData?.paymentState) &&
    canPayInvoice;
  const isReadOnly =
    isCanceledOrCanceling(documentData?.paymentState) || isNotNil(documentData?.cancelledAt);

  const paymentDialogDocumentId = match(documentType)
    .with('PROFORMA', always({proformaId: documentId}))
    .with('CORRECTIVE_TAX_DOCUMENT', always({correctiveTaxDocumentId: documentId}))
    .otherwise(always({invoiceId: documentId}));

  const paymentFormTitle = match(documentType)
    .with(
      'CORRECTIVE_TAX_DOCUMENT',
      always(
        i18n.t('entity.invoice.labels.paymentOfCorrectiveTaxDocument', {
          number: documentData?.number,
        })
      )
    )
    .otherwise(
      always(
        i18n.t('entity.invoice.labels.paymentOfInvoice', {
          number: documentData?.number,
        })
      )
    );

  const cancelActionTitle = match(documentType)
    .with(
      'CORRECTIVE_TAX_DOCUMENT',
      always(i18n.t('entity.invoice.actions.cancelCorrectiveTaxDocument'))
    )
    .otherwise(always(i18n.t('entity.invoice.actions.cancelInvoice')));

  const isMarginDocument = documentData?.items?.some((item) => item.type === 'margin');

  const canShowMarginDocumentActions = isMarginDocument && canRenderMarginInvoicePrint;

  // Margin documents can be rendered either as a standard or as a margin document.
  const createOutputAction = (
    output: DownloadAccountingDocument,
    props: Pick<ButtonActionProps, 'title' | 'leftIcon' | 'isLoading' | 'data-testid'> & {
      standardLabel: string;
      marginLabel: string;
    }
  ): Action =>
    canShowMarginDocumentActions
      ? {
          title: props.title,
          leftIcon: props.leftIcon,
          type: 'dropdown-button',
          menuItems: [
            {
              onClick: () => outputDocument(output, false),
              label: props.standardLabel,
              'data-testid': 'standard',
            },
            {
              onClick: () => outputDocument(output, true),
              label: props.marginLabel,
              'data-testid': 'margin',
            },
          ],
          variant: 'link',
          isLoading: props.isLoading,
          'data-testid': props['data-testid'],
        }
      : {
          title: props.title,
          leftIcon: props.leftIcon,
          onClick: () => outputDocument(output, false),
          isLoading: props.isLoading,
          type: 'button',
          variant: 'link',
          'data-testid': props['data-testid'],
        };

  const downloadAction = createOutputAction(downloadDocument, {
    title: i18n.t('general.actions.generatePDF'),
    leftIcon: 'editor/insert_drive_file',
    isLoading: isDownloading,
    'data-testid': testIds.accounting.invoiceDetail('downloadPdf'),
    standardLabel: i18n.t('entity.invoice.actions.downloadStandardInvoice'),
    marginLabel: i18n.t('entity.invoice.actions.downloadMarginInvoice'),
  });
  const downloadDocxAction = createOutputAction(downloadDocumentAsDocx, {
    title: DOCX_ACTION_TITLE,
    leftIcon: 'file/download',
    isLoading: isDownloadingDocx,
    'data-testid': testIds.accounting.invoiceDetail('downloadDocx'),
    standardLabel: i18n.t('entity.invoice.actions.downloadStandardInvoice'),
    marginLabel: i18n.t('entity.invoice.actions.downloadMarginInvoice'),
  });
  const printAction = createOutputAction(printDocument, {
    title: i18n.t('general.actions.printPDF'),
    leftIcon: 'action/print',
    isLoading: isPrinting,
    'data-testid': testIds.accounting.invoiceDetail('printPdf'),
    standardLabel: i18n.t('entity.invoice.actions.printStandardInvoice'),
    marginLabel: i18n.t('entity.invoice.actions.printMarginInvoice'),
  });

  // The DOCX action stays directly left of the PDF download in every action set, so the output
  // actions keep their order however many document actions precede them. Read-only documents
  // return the output actions alone, so the download permission is checked here for both sets.
  const outputActions = buildArray<Action>()
    .when(
      canDownloadDocuments &&
        isNotNil(documentData) &&
        hasAccountingDocumentDocxRendition(documentType, documentData.documentSource?.moduleSource),
      downloadDocxAction
    )
    .when(canDownloadDocuments, downloadAction, printAction);

  if (isReadOnly) {
    return [outputActions];
  }

  const actions = buildArray<Action>()
    .when(
      !isDocumentExternal(documentData) &&
        documentData?.cancellable &&
        isDocumentFromAccountingModule &&
        canCancelInvoice,
      {
        title: cancelActionTitle,
        leftIcon: 'navigation/cancel',
        type: 'button',
        onClick: handleCancelDocument,
        isLoading: isCancelling,
        variant: 'dangerLink',
        size: 'small',
      }
    )
    .when(
      INVOICE_TYPES_THAT_CAN_CREATE_CORRECTIVE_DOCUMENT.includes(documentType) &&
        isDocumentFromAccountingModule &&
        canCreateCorrectiveTaxDocumentInvoice,
      {
        title: i18n.t('entity.invoice.actions.createCorrectiveTaxDocument'),
        leftIcon: 'editor/insert_drive_file',
        type: 'button',
        onClick: () =>
          openConfirmDialog({
            onConfirm: handleOpenNewCorrectiveTaxDocument,
            text: i18n.t('entity.invoice.notifications.generateCorrectiveTaxDocumentHelperText', {
              invoiceNumber: documentData?.number,
            }),
            'data-testid': testIds.accounting.invoiceDetail('generateCorrectiveTaxDoc'),
          }),
        variant: 'link',
      }
    )
    .when(INVOICE_TYPES_THAT_CAN_CHANGE_BILLING_INFO.includes(documentType) && canCreateInvoice, {
      leftIcon: 'image/edit',
      variant: 'link',
      type: 'button',
      title: i18n.t('entity.customer.actions.changeBillingInformation'),
      onClick: openBillingInformationDialog,
    })
    .when(canBePaid && isDocumentFromAccountingModule, {
      leftIcon: 'action/payment',
      variant: 'link',
      type: 'button',
      title: i18n.t('entity.order.actions.pay'),
      onClick: () =>
        openDialog(
          <InvoicePaymentForm
            {...paymentDialogDocumentId}
            data-testid={testIds.accounting.invoiceDetail('paymentDialog')}
          />,
          {
            title: paymentFormTitle,
            'data-testid': testIds.accounting.invoiceDetail('paymentDialog'),
            scrollBehavior: 'outside',
          }
        ),
    })
    .add(...outputActions);

  return [actions];
};

const isDocumentExternal = (documentData: Record<string, unknown> | Nullish) =>
  (documentData as TaxDocumentForPaymentResponseBody)?.source === 'external';
